import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  TextField, 
  Typography, 
  Box, 
  Link, 
  InputAdornment, 
  Alert,
  IconButton,
  Card,
  CardContent,
  Button,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Image as ImageIcon,
  Delete,
  PhotoCamera,
  Visibility,
} from '@mui/icons-material';
import axios from 'axios';
import { BandRecommendationFormData } from '../../types/BandRecommendations';
import FocalPointPicker from './FocalPointPicker';

interface AdditionalMediaProps {
  formData: BandRecommendationFormData;
  setFormData: React.Dispatch<React.SetStateAction<BandRecommendationFormData>>;
}

// Custom TikTok icon since MUI doesn't have one
const TikTokIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7.56a8.16 8.16 0 0 0 4.77 1.53v-3.4a4.85 4.85 0 0 1-1.04 0z"/>
  </svg>
);

const AdditionalMedia: React.FC<AdditionalMediaProps> = ({ formData, setFormData }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const cleanHandle = (handle: string) => {
    let cleaned = handle.replace(/^@/, '').trim();
    if (cleaned.includes('facebook.com/')) {
      cleaned = cleaned.split('facebook.com/')[1]?.split('/')[0] || cleaned;
    }
    if (cleaned.includes('instagram.com/')) {
      cleaned = cleaned.split('instagram.com/')[1]?.split('/')[0] || cleaned;
    }
    if (cleaned.includes('twitter.com/')) {
      cleaned = cleaned.split('twitter.com/')[1]?.split('/')[0] || cleaned;
    }
    if (cleaned.includes('tiktok.com/@')) {
      cleaned = cleaned.split('tiktok.com/@')[1]?.split('/')[0] || cleaned;
    }
    return cleaned;
  };

  const handleSocialMediaChange = (
    name: 'instagramHandle' | 'twitterHandle' | 'facebookPage' | 'tikTokHandle',
    value: string
  ) => {
    const cleanedValue = cleanHandle(value);
    setFormData(prev => ({ ...prev, [name]: cleanedValue }));
  };

  // Same upload pattern as BandInfo — posts to /api/upload/band, gets back imageUrl
  // The only difference is we push to additionalImages array instead of replacing mainImage
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    if (file.size > 50 * 1024 * 1024) {
      setUploadError('File size exceeds 50MB limit');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      setUploadError('Please upload a JPEG, PNG, or GIF file');
      return;
    }

    setUploading(true);

    const formDataPayload = new FormData();
    formDataPayload.append('image', file);

    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/upload/band`,
        formDataPayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          withCredentials: true,
        }
      );

      setFormData(prevData => ({
        ...prevData,
        additionalImages: [
          ...(prevData.additionalImages || []),
          response.data.imageUrl,
        ],
      }));

      // Reset the file input so the same file can be re-selected if needed
      event.target.value = '';

    } catch (error: any) {
      console.error('Error uploading image:', error);
      if (error.response?.status === 401) {
        setUploadError('Please log in to upload images');
      } else if (error.response?.status === 413) {
        setUploadError('Image file is too large');
      } else {
        setUploadError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to upload image. Please try again.'
        );
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    const currentImages = formData.additionalImages || [];
    setFormData(prev => ({
      ...prev,
      additionalImages: currentImages.filter((_, i) => i !== index)
    }));
  };

  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'instagram': return <InstagramIcon />;
      case 'twitter':   return <TwitterIcon />;
      case 'facebook':  return <FacebookIcon />;
      case 'tiktok':    return <TikTokIcon />;
      default:          return null;
    }
  };

  const getSocialUrl = (platform: string, handle: string) => {
    if (!handle) return null;
    const baseUrls: Record<string, string> = {
      instagram: 'https://instagram.com/',
      twitter:   'https://twitter.com/',
      facebook:  'https://facebook.com/',
      tiktok:    'https://tiktok.com/@',
    };
    return baseUrls[platform] + handle;
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>

      {/* Additional Images Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="primary" />
            Band Images
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Upload additional photos — live shots, promotional images, or behind-the-scenes content.
            These will appear in the band's image gallery alongside the main image.
          </Typography>

          {/* Main image focal point — re-adjustable here as well */}
          {formData.mainImage && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Main band image — adjust focal point:
              </Typography>
              <FocalPointPicker
                imageUrl={formData.mainImage}
                value={formData.mainImagePosition || '50% 50%'}
                onChange={(pos) => setFormData(prev => ({ ...prev, mainImagePosition: pos }))}
              />
            </Box>
          )}

          {/* Upload button — same pattern as BandInfo */}
          <Box sx={{ mt: 2, mb: 3 }}>
            <input
              accept="image/jpeg,image/png,image/gif"
              style={{ display: 'none' }}
              id="additional-image-upload"
              type="file"
              onChange={handleFileChange}
              disabled={uploading}
            />
            <label htmlFor="additional-image-upload">
              <Button
                variant="contained"
                component="span"
                disabled={uploading}
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <PhotoCamera />}
                sx={{ py: 1.5, minWidth: 220 }}
              >
                {uploading ? 'Uploading...' : 'Upload Additional Image'}
              </Button>
            </label>

            {uploadError && (
              <Alert severity="error" onClose={() => setUploadError(null)} sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}
          </Box>

          {/* Uploaded additional images — each with its own focal point picker */}
          {formData.additionalImages && formData.additionalImages.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Additional Images ({formData.additionalImages.length})
              </Typography>

              <Grid container spacing={3}>
                {formData.additionalImages.map((url, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card variant="outlined" sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Image {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeImage(index)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      <FocalPointPicker
                        imageUrl={url}
                        value={(formData.additionalImagePositions || [])[index] || '50% 50%'}
                        onChange={(pos) => {
                          const positions = [...(formData.additionalImagePositions || [])];
                          positions[index] = pos;
                          setFormData(prev => ({ ...prev, additionalImagePositions: positions }));
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Alert severity="info">
              No additional images uploaded yet.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Social Media Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TwitterIcon color="primary" />
            Social Media Profiles
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            Add the band's social media handles. Just the username — no need for the full URL.
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {[
              { key: 'instagramHandle', label: 'Instagram',  platform: 'instagram', placeholder: 'username' },
              { key: 'twitterHandle',   label: 'Twitter/X',  platform: 'twitter',   placeholder: 'username' },
              { key: 'facebookPage',    label: 'Facebook',   platform: 'facebook',  placeholder: 'username or page name' },
              { key: 'tikTokHandle',    label: 'TikTok',     platform: 'tiktok',    placeholder: 'username' },
            ].map(({ key, label, platform, placeholder }) => {
              const fieldValue = formData[key as keyof BandRecommendationFormData] as string || '';
              const profileUrl = getSocialUrl(platform, fieldValue);

              return (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    name={key}
                    label={label}
                    value={fieldValue}
                    onChange={(e) => handleSocialMediaChange(
                      key as 'instagramHandle' | 'twitterHandle' | 'facebookPage' | 'tikTokHandle',
                      e.target.value
                    )}
                    variant="outlined"
                    fullWidth
                    placeholder={placeholder}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getSocialIcon(platform)}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ autoComplete: 'off' }}
                  />
                  {/* Show a plain preview link when a handle exists — no fake validation */}
                  {profileUrl && (
                    <FormHelperText>
                      <Link
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
                      >
                        <Visibility fontSize="small" />
                        Preview profile
                      </Link>
                    </FormHelperText>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

    </Box>
  );
};

export default AdditionalMedia;